import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApartmentOutlined, CloudSyncOutlined, DeleteOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Segmented,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd'
import { adminApi, adminLongRunningApi, formatApiError, PAGE_SIZE, type PageResult } from '../../api'
import {
  RelationsReviewDrawer,
  type ReviewItemDraft,
} from '../../components/RelationsReviewDrawer'
import {
  RelationsConfirmedDrawer,
  type ConfirmedReviewItem,
} from '../../components/RelationsConfirmedDrawer'
import { AgentPromptLink } from './AgentPromptLink'
import {
  KNOWLEDGE_SCOPE_OPTIONS_SHORT,
  type KnowledgeScope,
  scopeQuery,
} from '../../constants/knowledgeScope'
import { PageHeader, tablePagination, usePagedQuery, formatDateOrDash } from '../../components/ui'
import './GraphManage.css'
import { GraphDataTab } from './GraphDataTab'
import { GraphLibraryManageTab } from './GraphLibraryManageTab'

const { Text, Title } = Typography

const DEFAULT_GRAPH_AGENT_CODE = 'graph_extraction'

type GraphCfg = {
  enabled: boolean
  agent_code: string
  max_text_chars: number
  max_passes: number
  max_relations_total: number
  min_relations: number
  rag_graph_filter_enabled: boolean
  rag_graph_filter_max_items: number
  system_prompt?: string
}

type AgentOption = {
  agent_code: string
  agent_name: string
}

type ReviewItem = ReviewItemDraft &
  ConfirmedReviewItem & {
    scope: KnowledgeScope
    index_status: string
    updated_at: string
  }

type ReviewListFilter = 'pending' | 'confirmed'

type ConfirmAllJobMeta = {
  job_id: string
  status: string
  progress: number
  total: number
  confirmed: number
  failed: number
  status_message: string
  error_message?: string | null
}

type ExtractAllJobMeta = {
  job_id: string
  status: string
  progress: number
  total: number
  extracted: number
  failed: number
  force?: boolean
  status_message: string
  error_message?: string | null
}

type GraphEsSyncResult = {
  mode?: string
  deleted_chunks: number
  indexed_chunks: number
  items_synced?: number
  items_unchanged?: number
  items_skipped: number
  items_failed: number
  orphans_purged?: number
  scopes: number
}

type GraphClearResult = {
  scopes: string[]
  items_cleared: number
  entity_contribs_deleted: number
  entities_deleted: number
  es_deleted_chunks: number
  es_deleted_entities: number
}

const emptyPage = (p: number): PageResult<ReviewItem> => ({
  items: [],
  total: 0,
  page: p,
  page_size: PAGE_SIZE,
})

function relationPreview(item: ReviewItem): string {
  const rows = item.relations_draft_json?.relations || []
  if (!rows.length) return '—'
  return rows
    .slice(0, 2)
    .map((r) => `${r.source_entity || '—'} ${r.relation || '关联'} ${r.target || '—'}`)
    .join('；')
}

function confirmedRelationPreview(item: ReviewItem): string {
  const rows = item.relations_json || []
  if (!rows.length) return '—'
  return rows
    .slice(0, 2)
    .map((r) => `${r.source_entity || '—'} ${r.relation || '关联'} ${r.target || '—'}`)
    .join('；')
}

export default function GraphManage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialScope =
    searchParams.get('scope') === 'personal' || searchParams.get('scope') === 'enterprise'
      ? (searchParams.get('scope') as KnowledgeScope)
      : 'enterprise'
  const tabParam = searchParams.get('tab')
  const initialTab =
    tabParam === 'libraries' || tabParam === 'extract' || tabParam === 'review' || tabParam === 'data'
      ? tabParam
      : 'libraries'

  const [scope, setScope] = useState<KnowledgeScope>(initialScope)
  const [reviewFilter, setReviewFilter] = useState<ReviewListFilter>('pending')
  const [pendingCount, setPendingCount] = useState(0)
  const [confirmedCount, setConfirmedCount] = useState(0)
  const [cfgLoading, setCfgLoading] = useState(false)
  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([])
  const [ragFilterSaving, setRagFilterSaving] = useState(false)
  const [syncEsLoading, setSyncEsLoading] = useState(false)
  const [clearLoading, setClearLoading] = useState(false)
  const [testExtractItemId, setTestExtractItemId] = useState<number | null>(null)
  const [testExtracting, setTestExtracting] = useState(false)
  const [testExtractHint, setTestExtractHint] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [reviewItem, setReviewItem] = useState<ReviewItem | null>(null)
  const [confirmedItem, setConfirmedItem] = useState<ReviewItem | null>(null)
  const [confirmAllJobId, setConfirmAllJobId] = useState<string | null>(null)
  const [confirmAllJob, setConfirmAllJob] = useState<ConfirmAllJobMeta | null>(null)
  const [confirmAllSubmitting, setConfirmAllSubmitting] = useState(false)
  const [extractAllForce, setExtractAllForce] = useState(false)
  const [extractAllCandidateCount, setExtractAllCandidateCount] = useState<number | null>(null)
  const [extractAllJobId, setExtractAllJobId] = useState<string | null>(null)
  const [extractAllJob, setExtractAllJob] = useState<ExtractAllJobMeta | null>(null)
  const [extractAllSubmitting, setExtractAllSubmitting] = useState(false)
  const [titleQ, setTitleQ] = useState('')
  const [appliedTitleQ, setAppliedTitleQ] = useState('')
  const [ragGraphFilterEnabled, setRagGraphFilterEnabled] = useState(false)
  const [ragGraphFilterMaxItems, setRagGraphFilterMaxItems] = useState(5)
  const [ragFilterMaxSaving, setRagFilterMaxSaving] = useState(false)
  const [cfgForm] = Form.useForm<GraphCfg>()
  const cfgEnabled = Form.useWatch('enabled', cfgForm) ?? true
  const boundAgentCode =
    (Form.useWatch('agent_code', cfgForm) as string | undefined) || DEFAULT_GRAPH_AGENT_CODE
  const scopeParams = useMemo(() => scopeQuery(scope), [scope])

  const agentSelectOptions = useMemo(
    () =>
      agentOptions.map((a) => ({
        value: a.agent_code,
        label: `${a.agent_name}（${a.agent_code}）`,
      })),
    [agentOptions],
  )

  const boundAgentName =
    agentOptions.find((a) => a.agent_code === boundAgentCode)?.agent_name || boundAgentCode

  const applyGraphCfg = useCallback(
    (data: GraphCfg) => {
      cfgForm.setFieldsValue({
        enabled: data.enabled,
        agent_code: data.agent_code || DEFAULT_GRAPH_AGENT_CODE,
        max_text_chars: data.max_text_chars,
        max_passes: data.max_passes,
        max_relations_total: data.max_relations_total,
        min_relations: data.min_relations,
      })
      setRagGraphFilterEnabled(Boolean(data.rag_graph_filter_enabled))
      setRagGraphFilterMaxItems(Number(data.rag_graph_filter_max_items) || 5)
    },
    [cfgForm],
  )

  useEffect(() => {
    void adminApi
      .get<{ items: AgentOption[] }>('/admin/agents/options', {
        params: { active_only: true, page_size: 200 },
      })
      .then((r) => setAgentOptions(r.data.items ?? []))
      .catch(() => setAgentOptions([]))
  }, [])

  useEffect(() => {
    adminApi
      .get<GraphCfg>('/admin/settings/graph-extraction')
      .then((r) => applyGraphCfg(r.data))
      .catch(() => {})
  }, [applyGraphCfg])

  const loadReviewCounts = useCallback(() => {
    Promise.all([
      adminApi.get<{ count: number }>('/admin/knowledge/relations/pending-count', {
        params: { scope },
      }),
      adminApi.get<{ count: number }>('/admin/knowledge/relations/confirmed-count', {
        params: { scope },
      }),
    ])
      .then(([pending, confirmed]) => {
        setPendingCount(pending.data.count)
        setConfirmedCount(confirmed.data.count)
      })
      .catch(() => {
        setPendingCount(0)
        setConfirmedCount(0)
      })
  }, [scope])

  const loadPage = useCallback(
    (p: number, ps: number) => {
      const params: Record<string, string | number> = {
        page: p,
        page_size: ps,
        relations_status: reviewFilter === 'pending' ? 'needs_review' : 'confirmed',
        ...scopeParams,
      }
      const q = appliedTitleQ.trim()
      if (q) params.q = q
      return adminApi
        .get<PageResult<ReviewItem>>('/admin/knowledge/items', { params })
        .then((r) => r.data)
        .catch((e) => {
          message.error(e.message)
          return emptyPage(p)
        })
    },
    [scopeParams, reviewFilter, appliedTitleQ],
  )

  const {
    page,
    setPage,
    total,
    items,
    loading,
    reload,
    handlePaginationChange,
    paginationOptions,
  } = usePagedQuery(loadPage)

  useEffect(() => {
    loadReviewCounts()
  }, [loadReviewCounts, page, loading, reviewFilter])

  const loadExtractAllCandidateCount = useCallback(() => {
    adminApi
      .get<{ count: number }>('/admin/knowledge/relations/extract-all/candidate-count', {
        params: { ...scopeParams, force: extractAllForce },
      })
      .then((r) => setExtractAllCandidateCount(r.data.count))
      .catch(() => setExtractAllCandidateCount(null))
  }, [scopeParams, extractAllForce])

  useEffect(() => {
    if (activeTab === 'extract') {
      loadExtractAllCandidateCount()
    }
  }, [activeTab, loadExtractAllCandidateCount])

  useEffect(() => {
    adminApi
      .get<{ job: ExtractAllJobMeta | null }>('/admin/knowledge/relations/extract-all/jobs/active', {
        params: scopeParams,
      })
      .then((r) => {
        const job = r.data.job
        if (job && (job.status === 'pending' || job.status === 'running')) {
          setExtractAllJobId(job.job_id)
          setExtractAllJob(job)
        }
      })
      .catch(() => {})
  }, [scopeParams])

  useEffect(() => {
    if (!extractAllJobId) {
      setExtractAllJob(null)
      return
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      adminApi
        .get<ExtractAllJobMeta>(`/admin/knowledge/relations/extract-all/jobs/${extractAllJobId}`)
        .then((r) => {
          if (cancelled) return
          setExtractAllJob(r.data)
          if (r.data.status === 'pending' || r.data.status === 'running') {
            timer = setTimeout(tick, 2000)
            return
          }
          if (r.data.status === 'completed') {
            if (r.data.failed > 0) {
              message.warning(
                r.data.status_message ||
                  `已抽取 ${r.data.extracted}/${r.data.total} 条，失败 ${r.data.failed} 条`,
              )
            } else {
              message.success(r.data.status_message || '全量抽取已完成')
            }
            setExtractAllJobId(null)
            setExtractAllJob(null)
            loadExtractAllCandidateCount()
            loadReviewCounts()
          } else if (r.data.status === 'failed') {
            message.error(r.data.error_message || r.data.status_message || '全量抽取失败', 8)
            setExtractAllJobId(null)
            setExtractAllJob(null)
          }
        })
        .catch((e) => {
          if (!cancelled) {
            message.error(formatApiError(e, '查询全量抽取进度失败'))
            setExtractAllJobId(null)
            setExtractAllJob(null)
          }
        })
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [extractAllJobId, loadExtractAllCandidateCount, loadReviewCounts])

  const onTabChange = (key: string) => {
    setActiveTab(key)
    const next = new URLSearchParams(searchParams)
    next.set('tab', key)
    setSearchParams(next, { replace: true })
  }

  const saveCfg = async (partial?: Partial<GraphCfg>) => {
    let values: GraphCfg
    try {
      values = await cfgForm.validateFields()
    } catch {
      return
    }
    const payload = {
      enabled: values.enabled,
      agent_code: values.agent_code,
      max_text_chars: values.max_text_chars,
      max_passes: values.max_passes,
      max_relations_total: values.max_relations_total,
      min_relations: values.min_relations,
      ...(partial || {}),
      rag_graph_filter_enabled: ragGraphFilterEnabled,
      rag_graph_filter_max_items: ragGraphFilterMaxItems,
    }
    setCfgLoading(true)
    try {
      const res = await adminApi.put<GraphCfg>('/admin/settings/graph-extraction', payload)
      applyGraphCfg(res.data)
      message.success('配置已保存')
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败')
    } finally {
      setCfgLoading(false)
    }
  }

  const onRagGraphFilterChange = (checked: boolean) => {
    setRagFilterSaving(true)
    adminApi
      .get<GraphCfg>('/admin/settings/graph-extraction')
      .then((cur) =>
        adminApi.put<GraphCfg>('/admin/settings/graph-extraction', {
          ...cur.data,
          rag_graph_filter_enabled: checked,
        }),
      )
      .then((r) => {
        const saved = Boolean(r.data.rag_graph_filter_enabled)
        applyGraphCfg(r.data)
        if (saved !== checked) {
          message.error('保存未生效，请确认当前账号为超级管理员后重试')
          return
        }
        message.success(checked ? '已开启 RAG 图谱增强' : '已关闭 RAG 图谱增强')
      })
      .catch((e) => {
        message.error(formatApiError(e, '保存失败'))
      })
      .finally(() => setRagFilterSaving(false))
  }

  const saveRagGraphFilterMaxItems = (value: number | null) => {
    const n = Math.max(1, Math.min(50, Number(value) || 5))
    setRagGraphFilterMaxItems(n)
    setRagFilterMaxSaving(true)
    adminApi
      .get<GraphCfg>('/admin/settings/graph-extraction')
      .then((cur) =>
        adminApi.put<GraphCfg>('/admin/settings/graph-extraction', {
          ...cur.data,
          rag_graph_filter_max_items: n,
        }),
      )
      .then((r) => {
        applyGraphCfg(r.data)
        message.success('限定文档上限已保存')
      })
      .catch((e) => {
        message.error(formatApiError(e, '保存失败'))
      })
      .finally(() => setRagFilterMaxSaving(false))
  }

  const runGraphEsSync = (fullRebuild: boolean) => {
    setSyncEsLoading(true)
    return adminLongRunningApi
      .post<GraphEsSyncResult>('/admin/knowledge/relations/sync-es', null, {
        params: { ...scopeParams, mode: fullRebuild ? 'full' : 'incremental' },
      })
      .then((r) => {
        const d = r.data
        const parts = [
          fullRebuild ? '全量重建完成' : '增量同步完成',
          `写入 ${d.indexed_chunks} 条图谱切片`,
          `同步 ${d.items_synced ?? '—'} 篇`,
        ]
        if (!fullRebuild && d.items_unchanged != null) {
          parts.push(`跳过 ${d.items_unchanged} 篇（已是最新）`)
        }
        if (d.orphans_purged) parts.push(`清理孤儿 ${d.orphans_purged} 篇`)
        if (d.deleted_chunks) parts.push(`删除 ${d.deleted_chunks} 条旧切片`)
        if (d.items_failed) parts.push(`失败 ${d.items_failed} 篇`)
        message.success(parts.join('，'))
      })
      .catch((e) => {
        message.error(formatApiError(e, '同步失败'))
        return Promise.reject(e)
      })
      .finally(() => setSyncEsLoading(false))
  }

  const syncGraphToEs = () => {
    Modal.confirm({
      title: '增量同步图谱到 ES？',
      content: (
        <>
          仅同步<strong>缺失</strong>或<strong>内容已变更</strong>的已审核条目，并清理搜索引擎中多余的图谱数据。
          <br />
          不影响正文切片；已是最新的条目不会重复写入。
        </>
      ),
      okText: '开始增量同步',
      cancelText: '取消',
      onOk: () => runGraphEsSync(false),
    })
  }

  const syncGraphToEsFull = () => {
    Modal.confirm({
      title: '强制全量重建图谱？',
      content: (
        <>
          将先<strong>删除</strong> ES 中当前范围（{scopeLabel}）的全部图谱关系切片，再按已审核条目
          全部重新写入。耗时较长，仅在增量对账异常时使用。
        </>
      ),
      okText: '开始全量重建',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => runGraphEsSync(true),
    })
  }

  const afterReview = () => {
    setReviewItem(null)
    void reload(page)
    loadReviewCounts()
  }

  const afterConfirmedChanged = () => {
    setConfirmedItem(null)
    void reload(page)
    loadReviewCounts()
  }

  const isPendingList = reviewFilter === 'pending'

  const scopeLabel =
    KNOWLEDGE_SCOPE_OPTIONS_SHORT.find((o) => o.value === scope)?.label || scope

  const lookupTestExtractItem = (id: number | null) => {
    if (!id || id < 1) {
      setTestExtractHint(null)
      return
    }
    adminApi
      .get<{ title: string; scope: string; index_status: string; relations_status?: string }>(
        `/admin/knowledge/items/${id}`,
      )
      .then((r) => {
        if (r.data.scope !== scope) {
          const label =
            KNOWLEDGE_SCOPE_OPTIONS_SHORT.find((o) => o.value === r.data.scope)?.label ||
            r.data.scope
          setTestExtractHint(`该文档属于「${label}」，请切换知识库范围`)
          return
        }
        const status = r.data.relations_status || 'none'
        setTestExtractHint(
          `${r.data.title} · 索引：${r.data.index_status || '—'} · 关系：${status}`,
        )
      })
      .catch(() => setTestExtractHint('未找到该 ID，或知识库范围不匹配'))
  }

  const runTestExtract = () => {
    const id = testExtractItemId
    if (!id || id < 1) {
      message.warning('请输入文档 ID')
      return
    }
    setTestExtracting(true)
    adminApi
      .post<{ queued: boolean; message?: string }>(
        `/admin/knowledge/items/${id}/relations/extract`,
        null,
        { params: scopeParams },
      )
      .then((r) => {
        if (!r.data.queued) {
          message.info(r.data.message || '该条目已在抽取队列中')
          return
        }
        message.success('已加入后台抽取队列，完成后请到「审核入库」查看')
        setActiveTab('review')
        const next = new URLSearchParams(searchParams)
        next.set('tab', 'review')
        setSearchParams(next, { replace: true })
        loadReviewCounts()
      })
      .catch((e) => message.error(formatApiError(e, '提交抽取失败')))
      .finally(() => setTestExtracting(false))
  }

  const clearGraphData = () => {
    const pwdRef = { current: '' }
    Modal.confirm({
      title: '清空图谱数据？',
      okText: '确认清空',
      okButtonProps: { danger: true },
      cancelText: '取消',
      content: (
        <>
          将清空<strong>{scopeLabel}</strong>知识库范围内的全部图谱数据，包括已审核/待审核关系、实体属性表、ES
          图谱索引，且<strong>不可恢复</strong>。
          <br />
          不会删除知识文档正文与检索切片。
          <Input.Password
            style={{ marginTop: 12 }}
            placeholder="请输入清空密码"
            onChange={(e) => {
              pwdRef.current = e.target.value
            }}
          />
        </>
      ),
      onOk: () => {
        if (!pwdRef.current.trim()) {
          message.error('请输入清空密码')
          return Promise.reject(new Error('missing password'))
        }
        setClearLoading(true)
        return adminApi
          .post<GraphClearResult>(
            '/admin/knowledge/graph/clear',
            { confirm: true, clear_password: pwdRef.current },
            { params: scopeParams },
          )
          .then((r) => {
            const d = r.data
            message.success(
              `已清空：${d.items_cleared} 篇条目关系，${d.entities_deleted} 个实体，ES 图谱 ${d.es_deleted_chunks} 条`,
            )
            loadReviewCounts()
            void reload(page)
          })
          .catch((e) => {
            message.error(formatApiError(e, '清空失败'))
            return Promise.reject(e)
          })
          .finally(() => setClearLoading(false))
      },
    })
  }

  const confirmAllJobRunning =
    confirmAllJob?.status === 'pending' || confirmAllJob?.status === 'running'

  const extractAllJobRunning =
    extractAllJob?.status === 'pending' || extractAllJob?.status === 'running'

  const startExtractAll = () => {
    if (extractAllJobRunning || extractAllSubmitting) return
    const count = extractAllCandidateCount ?? 0
    Modal.confirm({
      title: '开始全量异步抽取？',
      okText: '开始抽取',
      cancelText: '取消',
      content: (
        <>
          将对<strong>{scopeLabel}</strong>范围内{' '}
          <strong>{extractAllForce ? '全部已索引' : '尚未抽取'}</strong> 的约{' '}
          <strong>{count}</strong> 篇文档依次调用模型抽取图谱，后台执行，可在此页查看进度。
          <br />
          完成后请到「审核入库」查看并确认；耗时取决于文档数量与模型速度。
        </>
      ),
      onOk: () => {
        setExtractAllSubmitting(true)
        return adminApi
          .post<ExtractAllJobMeta>('/admin/knowledge/relations/extract-all', null, {
            params: { ...scopeParams, force: extractAllForce },
          })
          .then((r) => {
            setExtractAllJobId(r.data.job_id)
            setExtractAllJob(r.data)
            if (r.data.total > 0) {
              message.info(`全量抽取任务已提交，共 ${r.data.total} 条…`)
            } else {
              message.info(r.data.status_message || '没有可抽取的条目')
            }
          })
          .catch((e) => {
            message.error(formatApiError(e, '提交全量抽取失败'))
            return Promise.reject(e)
          })
          .finally(() => setExtractAllSubmitting(false))
      },
    })
  }

  useEffect(() => {
    adminApi
      .get<{ job: ConfirmAllJobMeta | null }>('/admin/knowledge/relations/confirm-all/jobs/active', {
        params: scopeParams,
      })
      .then((r) => {
        const job = r.data.job
        if (job && (job.status === 'pending' || job.status === 'running')) {
          setConfirmAllJobId(job.job_id)
          setConfirmAllJob(job)
        }
      })
      .catch(() => {})
  }, [scopeParams])

  useEffect(() => {
    if (!confirmAllJobId) {
      setConfirmAllJob(null)
      return
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const tick = () => {
      if (cancelled) return
      adminApi
        .get<ConfirmAllJobMeta>(`/admin/knowledge/relations/confirm-all/jobs/${confirmAllJobId}`)
        .then((r) => {
          if (cancelled) return
          setConfirmAllJob(r.data)
          if (r.data.status === 'pending' || r.data.status === 'running') {
            timer = setTimeout(tick, 2000)
            return
          }
          if (r.data.status === 'completed') {
            if (r.data.failed > 0) {
              message.warning(
                r.data.status_message ||
                  `已审核 ${r.data.confirmed}/${r.data.total} 条，失败 ${r.data.failed} 条`,
              )
            } else {
              message.success(r.data.status_message || '已全部审核通过')
            }
            setConfirmAllJobId(null)
            setConfirmAllJob(null)
            setReviewItem(null)
            void reload(1)
            loadReviewCounts()
          } else if (r.data.status === 'failed') {
            message.error(r.data.error_message || r.data.status_message || '批量审核失败', 8)
            setConfirmAllJobId(null)
            setConfirmAllJob(null)
          }
        })
        .catch((e) => {
          if (!cancelled) {
            message.error(formatApiError(e, '查询批量审核进度失败'))
            setConfirmAllJobId(null)
            setConfirmAllJob(null)
          }
        })
    }

    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [confirmAllJobId, loadReviewCounts, reload])

  const confirmAllPending = () => {
    if (pendingCount < 1 || confirmAllJobRunning || confirmAllSubmitting) return
    Modal.confirm({
      title: '全部审核通过？',
      content: (
        <>
          将把当前范围「{scopeLabel}」下 <strong>{pendingCount}</strong>{' '}
          条待审核条目，按 AI 抽取结果原样写入知识图谱（后台异步执行，可在此页查看进度）。
          <br />
          此操作不可撤销，请确认抽取质量可接受。
        </>
      ),
      okText: '全部通过',
      cancelText: '取消',
      onOk: () => {
        setConfirmAllSubmitting(true)
        return adminApi
          .post<ConfirmAllJobMeta>('/admin/knowledge/relations/confirm-all', null, {
            params: scopeParams,
          })
          .then((r) => {
            setConfirmAllJobId(r.data.job_id)
            setConfirmAllJob(r.data)
            message.info('批量审核任务已提交，正在后台执行…')
          })
          .catch((e) => {
            message.error(formatApiError(e, '提交批量审核失败'))
            return Promise.reject(e)
          })
          .finally(() => setConfirmAllSubmitting(false))
      },
    })
  }

  const onExtractScopeChange = (v: string | number) => {
    setScope(v as KnowledgeScope)
    lookupTestExtractItem(testExtractItemId)
  }

  const extractTab = (
    <>
      <div className="graph-manage-toolbar">
        <Text type="secondary">知识库范围</Text>
        <Segmented
          value={scope}
          options={KNOWLEDGE_SCOPE_OPTIONS_SHORT}
          onChange={onExtractScopeChange}
        />
      </div>

      <Row gutter={[16, 16]} className="graph-manage-task-row">
        <Col xs={24} lg={12}>
          <Card size="small" title="全量异步抽取" className="graph-manage-task-card">
            <Text type="secondary" className="graph-manage-card-desc">
              对当前知识库内全部已索引文档依次执行图谱抽取（后台线程，不阻塞页面）。默认跳过已审核/待审核条目；开启「含已审核」可强制重抽。
            </Text>
            <div className="graph-manage-task-actions">
              <Space size={4} className="graph-manage-force-switch">
                <Switch
                  checked={extractAllForce}
                  disabled={extractAllJobRunning || extractAllSubmitting}
                  onChange={(checked) => setExtractAllForce(checked)}
                />
                <Text>含已审核/待审核（强制重抽）</Text>
              </Space>
              <Button
                type="primary"
                icon={<CloudSyncOutlined />}
                loading={extractAllSubmitting || extractAllJobRunning}
                disabled={
                  extractAllJobRunning || extractAllSubmitting || (extractAllCandidateCount ?? 0) < 1
                }
                onClick={startExtractAll}
              >
                开始全量抽取
                {extractAllCandidateCount != null ? `（${extractAllCandidateCount} 篇）` : ''}
              </Button>
            </div>
            {extractAllJob && extractAllJobRunning ? (
              <Alert
                type="info"
                showIcon
                className="graph-manage-job-alert"
                title="全量抽取进行中"
                description={
                  <Space direction="vertical" style={{ width: '100%' }} size={4}>
                    <Text type="secondary">{extractAllJob.status_message}</Text>
                    <Progress
                      percent={extractAllJob.progress}
                      status="active"
                      format={() =>
                        extractAllJob.total > 0
                          ? `${extractAllJob.extracted + extractAllJob.failed}/${extractAllJob.total}`
                          : `${extractAllJob.progress}%`
                      }
                    />
                  </Space>
                }
              />
            ) : null}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="按文档 ID 试抽取" className="graph-manage-task-card">
            <Text type="secondary" className="graph-manage-card-desc">
              指定一篇已索引文档进行图谱抽取（强制重抽，覆盖待审核/已审核状态）。完成后在「审核入库」中查看与确认。
            </Text>
            <div className="graph-manage-test-extract">
              <InputNumber
                min={1}
                precision={0}
                placeholder="文档 ID"
                className="graph-manage-test-extract-input"
                value={testExtractItemId}
                onChange={(v) => {
                  const id = v != null ? Number(v) : null
                  setTestExtractItemId(id)
                  lookupTestExtractItem(id)
                }}
              />
              <Button type="primary" loading={testExtracting} onClick={runTestExtract}>
                开始抽取
              </Button>
            </div>
            {testExtractHint ? (
              <Text
                type={testExtractHint.startsWith('未找到') ? 'danger' : 'secondary'}
                className="graph-manage-test-hint"
              >
                {testExtractHint}
              </Text>
            ) : null}
          </Card>
        </Col>
      </Row>

      <Card size="small" title="抽取参数" className="graph-manage-params-card">
        <Form
          form={cfgForm}
          layout="vertical"
          className="graph-manage-params-form"
          initialValues={{ enabled: true, agent_code: DEFAULT_GRAPH_AGENT_CODE }}
        >
          <div className="graph-manage-params-top">
            <div className="graph-manage-switch-tile">
              <div className="graph-manage-switch-head">
                <Text strong>启用 AI 抽取</Text>
                <Form.Item name="enabled" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>
              <Text type="secondary" className="graph-manage-switch-desc">
                开启后，文档索引完成（含已审核条目重新索引）将自动异步抽取关系；完成后进入「审核入库」。
              </Text>
            </div>
          </div>

          <Form.Item
            name="agent_code"
            label="绑定智能体"
            rules={[{ required: true, message: '请选择智能体' }]}
            extra="抽取使用的提示词与模型取自该智能体；默认可选内置「图谱抽取」。"
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={agentSelectOptions}
              placeholder="选择智能体"
              disabled={!cfgEnabled}
            />
          </Form.Item>

          {boundAgentCode ? (
            <div className="graph-manage-agent-panel">
              <AgentPromptLink
                key={boundAgentCode}
                agentCode={boundAgentCode}
                agentName={boundAgentName}
                extra="提示词可用占位符 {min_relations}、{max_relations}（与下方「首段最少关系数」联动）。"
              />
              <Typography.Paragraph type="secondary" className="graph-manage-agent-tip">
                编辑绑定智能体：<Link to="/manage/agents">智能体列表</Link>
                {' · '}
                标识 <Typography.Text code>{boundAgentCode}</Typography.Text>
              </Typography.Paragraph>
            </div>
          ) : null}

          <Title level={5} className="graph-manage-section-title">
            分段与合并
          </Title>
          <Text type="secondary" className="graph-manage-section-desc">
            长文档按段多次调用模型，结果去重合并；需在「模型管理」或绑定智能体中配置对话 API。
          </Text>

          <Row gutter={[16, 0]} className="graph-manage-params-grid">
            <Col xs={24} md={12}>
              <Form.Item
                name="max_text_chars"
                label="每段正文字数"
                extra="单段默认 6000，最大 20000。"
              >
                <InputNumber min={1000} max={20000} step={500} disabled={!cfgEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="max_passes"
                label="最多分段次数"
                extra="单篇最多调用模型的次数。"
              >
                <InputNumber min={1} max={50} disabled={!cfgEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="max_relations_total"
                label="合并关系上限"
                extra="分段去重后的总关系数上限，默认 500。"
              >
                <InputNumber min={50} max={1000} step={50} disabled={!cfgEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="min_relations"
                label="首段最少关系数"
                extra="第 1 段或短文档的目标条数。"
              >
                <InputNumber min={1} max={50} disabled={!cfgEnabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <div className="graph-manage-params-footer">
            <Button
              type="primary"
              icon={<ApartmentOutlined />}
              loading={cfgLoading}
              onClick={() => void saveCfg()}
            >
              保存配置
            </Button>
          </div>
        </Form>
      </Card>
    </>
  )

  const reviewTab = (
    <>
      <div className="graph-manage-toolbar graph-manage-review-toolbar">
        <Segmented
          value={scope}
          options={KNOWLEDGE_SCOPE_OPTIONS_SHORT}
          onChange={(v) => {
            setScope(v as KnowledgeScope)
            setPage(1)
            setTitleQ('')
            setAppliedTitleQ('')
            setReviewItem(null)
            setConfirmedItem(null)
          }}
        />
        <Segmented
          value={reviewFilter}
          options={[
            { label: `待审核 (${pendingCount})`, value: 'pending' },
            { label: `已审核 (${confirmedCount})`, value: 'confirmed' },
          ]}
          onChange={(v) => {
            setReviewFilter(v as ReviewListFilter)
            setPage(1)
            setTitleQ('')
            setAppliedTitleQ('')
            setReviewItem(null)
            setConfirmedItem(null)
          }}
        />
        <Input.Search
          allowClear
          placeholder="搜索文档标题"
          style={{ width: 220 }}
          value={titleQ}
          onChange={(e) => setTitleQ(e.target.value)}
          onSearch={(v) => {
            setTitleQ(v)
            setAppliedTitleQ(v)
            setPage(1)
          }}
        />
        {isPendingList ? (
          <Button
            type="primary"
            disabled={pendingCount < 1 || loading || confirmAllJobRunning || confirmAllSubmitting}
            loading={confirmAllSubmitting || confirmAllJobRunning}
            onClick={confirmAllPending}
          >
            全部审核通过
          </Button>
        ) : null}
        <Button icon={<CloudSyncOutlined />} loading={syncEsLoading} onClick={syncGraphToEs}>
          增量同步图谱
        </Button>
        <Button loading={syncEsLoading} onClick={syncGraphToEsFull}>
          全量重建
        </Button>
        <Button danger icon={<DeleteOutlined />} loading={clearLoading} onClick={clearGraphData}>
          清空图谱数据
        </Button>
      </div>

      {confirmAllJob && confirmAllJobRunning ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title="批量审核入库进行中"
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">{confirmAllJob.status_message}</Text>
              <Progress
                percent={confirmAllJob.progress}
                status="active"
                format={() =>
                  confirmAllJob.total > 0
                    ? `${confirmAllJob.confirmed + confirmAllJob.failed}/${confirmAllJob.total}`
                    : `${confirmAllJob.progress}%`
                }
              />
            </Space>
          }
        />
      ) : null}

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={items}
        locale={{
          emptyText: appliedTitleQ.trim()
            ? '未找到匹配标题的条目，请换个关键词试试。'
            : isPendingList
              ? '暂无待审核条目。新文档索引并自动抽取后，会出现在此列表。'
              : '暂无已审核条目。审核通过或手动维护关系后，会出现在此列表。',
        }}
        pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
        columns={
          isPendingList
            ? [
                {
                  title: '文档标题',
                  dataIndex: 'title',
                  ellipsis: true,
                  render: (v: string) => <Text strong>{v}</Text>,
                },
                {
                  title: '实体数',
                  width: 72,
                  align: 'center' as const,
                  render: (_, r) => r.relations_draft_json?.entities?.length ?? 0,
                },
                {
                  title: '关系数',
                  width: 72,
                  align: 'center' as const,
                  render: (_, r) => r.relations_draft_json?.relations?.length ?? 0,
                },
                {
                  title: '抽取摘要',
                  ellipsis: true,
                  render: (_, r) => (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {relationPreview(r)}
                    </Text>
                  ),
                },
                {
                  title: '模型',
                  width: 120,
                  ellipsis: true,
                  render: (_, r) =>
                    r.relations_draft_json?.model ? (
                      <Tag style={{ margin: 0 }}>{r.relations_draft_json.model}</Tag>
                    ) : (
                      '—'
                    ),
                },
                {
                  title: '抽取时间',
                  width: 160,
                  render: (_, r) => {
                    const raw = r.relations_draft_json?.extracted_at
                    return raw ? formatDateOrDash(raw) : '—'
                  },
                },
                {
                  title: '操作',
                  width: 72,
                  fixed: 'right' as const,
                  render: (_, r) => (
                    <Button type="link" size="small" onClick={() => setReviewItem(r)}>
                      审核
                    </Button>
                  ),
                },
              ]
            : [
                {
                  title: '文档标题',
                  dataIndex: 'title',
                  ellipsis: true,
                  render: (v: string) => <Text strong>{v}</Text>,
                },
                {
                  title: '状态',
                  width: 80,
                  render: () => <Tag color="success">已审核</Tag>,
                },
                {
                  title: '实体数',
                  width: 72,
                  align: 'center' as const,
                  render: (_, r) => {
                    const fromDraft = r.relations_draft_json?.entities?.length
                    if (fromDraft != null) return fromDraft
                    const names = new Set<string>()
                    for (const row of r.relations_json || []) {
                      if (row.source_entity) names.add(row.source_entity)
                      if (row.target) names.add(row.target)
                    }
                    return names.size
                  },
                },
                {
                  title: '关系数',
                  width: 72,
                  align: 'center' as const,
                  render: (_, r) => r.relations_json?.length ?? 0,
                },
                {
                  title: '关系摘要',
                  ellipsis: true,
                  render: (_, r) => (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {confirmedRelationPreview(r)}
                    </Text>
                  ),
                },
                {
                  title: '更新时间',
                  width: 160,
                  render: (_, r) => formatDateOrDash(r.updated_at),
                },
                {
                  title: '操作',
                  width: 72,
                  fixed: 'right' as const,
                  render: (_, r) => (
                    <Button type="link" size="small" onClick={() => setConfirmedItem(r)}>
                      查看
                    </Button>
                  ),
                },
              ]
        }
      />

      <RelationsReviewDrawer
        open={reviewItem != null}
        item={reviewItem}
        scopeParams={scopeParams}
        onClose={() => setReviewItem(null)}
        onDone={afterReview}
      />
      <RelationsConfirmedDrawer
        open={confirmedItem != null}
        item={confirmedItem}
        scopeParams={scopeParams}
        onClose={() => setConfirmedItem(null)}
        onChanged={afterConfirmedChanged}
      />
    </>
  )

  const reviewLabel =
    pendingCount > 0
      ? `审核入库 (待${pendingCount})`
      : confirmedCount > 0
        ? `审核入库 (已${confirmedCount})`
        : '审核入库'

  return (
    <div className="graph-manage">
      <PageHeader
        title="知识图谱管理"
        description="维护独立图谱库、构建版本和权限；旧版关系抽取与图谱数据继续保留。"
      />

      {activeTab !== 'libraries' ? <Card size="small" title="RAG 图谱增强" className="graph-manage-rag-card">
        <Text type="secondary" className="graph-manage-card-desc">
          开启后：问句命中图谱实体时辅助检索（写入 ES 的图谱切片 + 实体过滤/加权）；关闭则与原有 RAG 完全一致。
        </Text>
        <div className="graph-manage-rag-row">
          <Space size="middle" align="center">
            <Switch
              checked={ragGraphFilterEnabled}
              loading={ragFilterSaving}
              checkedChildren="开"
              unCheckedChildren="关"
              onChange={onRagGraphFilterChange}
            />
            <Text>{ragGraphFilterEnabled ? '已开启' : '已关闭'}</Text>
          </Space>
          <div className="graph-manage-rag-limit">
            <Text type="secondary">限定文档上限</Text>
            <InputNumber
              min={1}
              max={50}
              precision={0}
              value={ragGraphFilterMaxItems}
              disabled={!ragGraphFilterEnabled || ragFilterMaxSaving}
              onChange={(v) => {
                if (v != null) setRagGraphFilterMaxItems(v)
              }}
              onBlur={() => saveRagGraphFilterMaxItems(ragGraphFilterMaxItems)}
            />
            <Text type="secondary" className="graph-manage-rag-hint">
              关联文档数不超过此值时只搜这些文档；超过则全库检索并将关联文档结果前置（默认 5）。
            </Text>
          </div>
        </div>
      </Card> : null}

      <Tabs
        className="graph-manage-tabs"
        activeKey={activeTab}
        onChange={onTabChange}
        destroyOnHidden={false}
        items={[
          { key: 'libraries', label: '图谱库', children: <GraphLibraryManageTab /> },
          { key: 'extract', label: '抽取配置', children: extractTab },
          { key: 'review', label: reviewLabel, children: reviewTab },
          {
            key: 'data',
            label: '图谱数据',
            children: <GraphDataTab scope={scope} onScopeChange={setScope} />,
          },
        ]}
      />
    </div>
  )
}
